/**
 * PasswordInputModal - Reusable password input modal
 * Used for operations requiring password authentication (device deletion, etc.)
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './PasswordInputModal.css';

const PasswordInputModal = ({ 
  isOpen, 
  onConfirm, 
  onCancel,
  title = 'Enter Password',
  message = 'Please enter your password to continue',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Cleanup password on unmount for security
  useEffect(() => {
    return () => {
      setPassword('');
      setShowPassword(false);
      setError('');
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setError('');
    onConfirm(password);
    setPassword('');  // Clear for security
  };

  const handleCancel = () => {
    setPassword('');
    setError('');
    onCancel();
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError('');  // Clear error on input
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-container modal-password" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={handleCancel}
            aria-label="Close"
          >
            <ion-icon name="close-outline" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <p className="modal-message">{message}</p>
          
          <form onSubmit={handleSubmit}>
            <div className="password-input-group">
              <label htmlFor="password-input" className="password-label">
                Password
              </label>
              <div className="password-input-container">
                <input
                  id="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  className={`password-input ${error ? 'error' : ''}`}
                  placeholder="Enter your password"
                  autoFocus
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <ion-icon name={showPassword ? 'eye-off-outline' : 'eye-outline'} />
                </button>
              </div>
              {error && <span className="password-error">{error}</span>}
            </div>

            {/* Footer buttons */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                {cancelText}
              </button>
              <button
                type="submit"
                className="btn btn-danger"
              >
                {confirmText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PasswordInputModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
};

export default PasswordInputModal;
